import { pointToLabel } from "../go/coordinates";
import { colorToStone, getGroup, opponent } from "../go/rules";
import type { Board, Color, Point } from "../go/types";
import { replayPvLine, type PvPly } from "./pvTrace";
import type { AlternativePreview, AnalysisResult, MoveGrade, MoveReport, PositionEval, ReportNote } from "./types";

export interface MoveReportInput {
  size: number;
  mover: Color; // who played the move being explained
  playedPoint: Point;
  playedLabel: string;
  boardBefore: Board | null; // position the move was played from (null if unknown)
  boardAfter: Board; // position after the move
  grade: MoveGrade | null;
  before: AnalysisResult | null; // analysis of the position before the move (best alternative)
  after: AnalysisResult; // analysis of the position after the move (opponent to play)
}

function sideName(color: Color): string {
  return color === "black" ? "Black" : "White";
}

function opponentName(mover: Color): string {
  return mover === "black" ? "White" : "Black";
}

// Plain-language board area for a point, using the display orientation (y = 0 is the top row).
export function regionName(point: Point, size: number): string {
  const third = size / 3;
  const col = point.x < third ? "left" : point.x >= size - third ? "right" : "";
  const row = point.y < third ? "upper" : point.y >= size - third ? "lower" : "";
  if (row && col) return `${row} ${col}`;
  if (row) return `${row} side`;
  if (col) return `${col} side`;
  return "centre";
}

function chebyshev(a: Point, b: Point): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

// Black-positive score phrase from the after-move root evaluation.
function scorePhrase(scoreLeadBlack: number | undefined): string {
  if (scoreLeadBlack === undefined || !Number.isFinite(scoreLeadBlack)) return "";
  const abs = Math.abs(scoreLeadBlack);
  if (abs < 0.5) return " The game stays roughly even.";
  return ` ${scoreLeadBlack > 0 ? "Black" : "White"} leads by about ${abs.toFixed(1)} points now.`;
}

function verdictText(input: MoveReportInput): string {
  const { grade, playedLabel, after } = input;
  if (!grade) {
    return `Here is how the game continues from ${playedLabel}.`;
  }

  const loss = grade.pointLoss;
  switch (grade.label) {
    case "Great":
      return grade.bestLabel === playedLabel
        ? `${playedLabel} is the engine's own top choice.${scorePhrase(after.rootScoreLead)}`
        : `Excellent — essentially nothing lost versus ${grade.bestLabel}.${scorePhrase(after.rootScoreLead)}`;
    case "Good":
      return `A fine move — only about ${loss.toFixed(1)} points off the engine's best.${scorePhrase(after.rootScoreLead)}`;
    case "Inaccuracy":
      return `Playable but loose — roughly ${loss.toFixed(1)} points slip away here.`;
    case "Mistake":
      return `This gives back about ${loss.toFixed(1)} points.`;
    case "Blunder":
      return grade.winrateDrop >= 0.25
        ? `A game-swinging mistake — your winning chances dropped about ${(grade.winrateDrop * 100).toFixed(0)}%.`
        : `A serious mistake — about ${loss.toFixed(1)} points lost.`;
  }
}

function isBadGrade(grade: MoveGrade | null): boolean {
  return Boolean(grade && (grade.label === "Inaccuracy" || grade.label === "Mistake" || grade.label === "Blunder"));
}

function sequenceText(plies: PvPly[], limit = 6): string {
  return plies.slice(0, limit).map((ply) => ply.label).join(" ");
}

// Describe the opponent's best answer to the played move by replaying its PV on the rules engine:
// tenuki (your move was ignorable), captures/ataris against the mover, counterplay for the mover,
// or a quiet developing line. This is the "how the enemy benefits or fights back" note.
function replyNote(input: MoveReportInput): { note: ReportNote; refutationIndex: number } | null {
  const { after, mover, playedPoint, size, boardAfter } = input;
  const reply = after.moves[0];
  if (!reply) return null;
  const bad = isBadGrade(input.grade);
  const opp = opponentName(mover);

  if (!reply.point) {
    return {
      refutationIndex: 0,
      note: {
        kind: "reply",
        text: `${opp}'s best answer is simply to pass — the position is settled locally.`
      }
    };
  }

  // Tenuki: the strongest answer is far from your move, so it did not demand a response.
  const distance = chebyshev(reply.point, playedPoint);
  const tenukiDistance = size >= 13 ? 5 : 4;
  const isTenuki = distance >= tenukiDistance;

  const { plies, finalBoard } = replayPvLine(boardAfter, size, opponent(mover), reply.pv ?? [reply.label]);
  const seq = plies.length > 0 ? sequenceText(plies) : reply.label;

  let oppCaptures = 0;
  let moverCaptures = 0;
  let firstOppCapture: PvPly | undefined;
  let firstOppAtari: PvPly | undefined;
  for (const ply of plies) {
    if (ply.color === mover) {
      moverCaptures += ply.captures;
    } else {
      oppCaptures += ply.captures;
      if (ply.captures > 0) firstOppCapture ??= ply;
      if (ply.ataris > 0) firstOppAtari ??= ply;
    }
  }

  let text: string;
  if (isTenuki) {
    text = `${opp}'s best reply ignores your move and takes ${reply.label} in the ${regionName(reply.point, size)} — ${
      bad ? "your move didn't demand an answer, so the opponent gets a free big point" : "there is nothing urgent left locally"
    }. Line: ${seq}.`;
  } else if (oppCaptures > 0 && firstOppCapture) {
    const trade = moverCaptures > 0 ? ` You get ${moverCaptures} stone${moverCaptures === 1 ? "" : "s"} back in the trade.` : "";
    text = `${bad ? "The punishment" : "The critical reply"} is ${reply.label}: in the line ${seq}, ${opp} captures ${oppCaptures} of your stone${
      oppCaptures === 1 ? "" : "s"
    } (starting at ${firstOppCapture.label}).${trade}`;
  } else if (firstOppAtari) {
    text = `${bad ? "Expect" : "The main reply is"} ${reply.label}: through ${seq}, ${opp} keeps your stones under atari at ${firstOppAtari.label} and holds the initiative.`;
  } else {
    const weak = weakestMoverGroupInLine(input, plies, finalBoard);
    if (weak) {
      text = `${opp} answers at ${reply.label} (${seq}) and your ${weak.stones}-stone group at ${weak.label} ends the line with only ${weak.liberties} libert${
        weak.liberties === 1 ? "y" : "ies"
      } — read its life before continuing.`;
    } else {
      text = `${opp}'s natural answer is ${reply.label}. Expected continuation: ${seq} — a calm line; compare what each side builds.`;
    }
  }

  return { refutationIndex: 0, note: { kind: "reply", text } };
}

// After the reply line is played out, check whether the played stone's group ends the line with
// dangerously few liberties (a concrete "this is what you must watch" fact).
function weakestMoverGroupInLine(
  input: MoveReportInput,
  plies: PvPly[],
  finalBoard: Board
): { label: string; stones: number; liberties: number } | null {
  if (plies.length === 0) return null;
  const group = getGroup(finalBoard, input.size, input.playedPoint);
  if (group.color !== colorToStone(input.mover)) return null; // the played stone was captured in the line
  if (group.liberties.size > 2) return null;
  return { label: input.playedLabel, stones: group.stones.length, liberties: group.liberties.size };
}

// What the engine wanted instead, with its concrete payoff read from the PV.
function alternativeNote(input: MoveReportInput): ReportNote | null {
  const { before, boardBefore, grade, mover, size, playedLabel } = input;
  if (!grade || grade.pointLoss < 0.5) return null;
  const best = before?.moves[0];
  if (!best || best.label === playedLabel) return null;

  let payoff = "";
  if (boardBefore && best.pv && best.pv.length > 0) {
    const { plies } = replayPvLine(boardBefore, size, mover, best.pv);
    if (plies.length > 1) {
      const moverCaptures = plies.filter((ply) => ply.color === mover).reduce((sum, ply) => sum + ply.captures, 0);
      const forcing = plies.find((ply) => ply.color === mover && ply.ataris > 0);
      const seq = sequenceText(plies);
      if (moverCaptures > 0) {
        payoff = ` Its line ${seq} captures ${moverCaptures} stone${moverCaptures === 1 ? "" : "s"}.`;
      } else if (forcing) {
        payoff = ` Its line ${seq} stays forcing (atari at ${forcing.label}).`;
      } else {
        payoff = ` Its line: ${seq}.`;
      }
    }
  }

  return {
    kind: "alternative",
    text: `Better was ${best.label}${
      best.point ? ` in the ${regionName(best.point, size)}` : ""
    } — worth about ${grade.pointLoss.toFixed(1)} points more.${payoff}`
  };
}

// If the played stone's own group is already counted against the mover by the ownership map,
// say so — the most common way a "reasonable-looking" move turns out bad.
export function playedGroupNote(
  boardAfter: Board,
  size: number,
  playedPoint: Point,
  mover: Color,
  ownershipAfter: number[] | undefined
): ReportNote | null {
  const group = getGroup(boardAfter, size, playedPoint);
  const liberties = group.liberties.size;

  // Atari is a pure rules fact — flag it even without an ownership map.
  if (liberties === 1) {
    return {
      kind: "group",
      text: `Your ${group.stones.length === 1 ? "stone" : `${group.stones.length}-stone group`} at ${pointToLabel(
        playedPoint,
        size
      )} is in atari — ${opponentName(mover)} can capture it at any time.`
    };
  }

  if (!ownershipAfter || ownershipAfter.length !== size * size) return null;
  const moverSign = mover === "black" ? 1 : -1;
  const mean = group.stones.reduce((sum, index) => sum + moverSign * ownershipAfter[index], 0) / group.stones.length;

  if (mean < -0.3) {
    return {
      kind: "group",
      text: `Careful: the engine already counts your ${group.stones.length}-stone group at ${pointToLabel(playedPoint, size)} as ${opponentName(
        mover
      )}'s area (${liberties} libert${liberties === 1 ? "y" : "ies"}) — it needs to live, run, or be given up on purpose.`
    };
  }
  if (mean < 0.05 && liberties <= 3) {
    return {
      kind: "group",
      text: `Your group at ${pointToLabel(playedPoint, size)} is unsettled (${liberties} liberties, contested ownership) — its life is the next big question.`
    };
  }
  return null;
}

// Counterfactual territory note: diff the ownership map after the played move against the map
// after the engine's best move, and localize where the points shifted. `afterOwnership` and
// `bestEval.ownership` are both Black-positive. `bestPoint` (when given) distinguishes "you
// passed up the influence the best move would have built" from "your move damaged this area":
// the local sum is gross influence, not net score, so the phrasing must not imply a net swing.
export function ownershipShiftNote(
  afterOwnership: number[] | undefined,
  bestEval: PositionEval,
  size: number,
  mover: Color,
  bestLabel: string,
  bestPoint: Point | null = null
): ReportNote | null {
  const area = size * size;
  if (!afterOwnership || afterOwnership.length !== area || bestEval.ownership.length !== area) return null;

  const moverSign = mover === "black" ? 1 : -1;
  // loss[i] > 0 where the played move leaves this point worse for the mover than the best move would.
  const loss = new Array<number>(area);
  for (let i = 0; i < area; i += 1) {
    loss[i] = moverSign * (bestEval.ownership[i] - afterOwnership[i]);
  }

  let hottest = -1;
  let hottestValue = 0.15; // ignore sub-noise shifts
  for (let i = 0; i < area; i += 1) {
    if (loss[i] > hottestValue) {
      hottestValue = loss[i];
      hottest = i;
    }
  }
  if (hottest < 0) return null;

  // Sum the shift in a neighbourhood around the hottest point to express it in points.
  const cx = hottest % size;
  const cy = Math.floor(hottest / size);
  let local = 0;
  for (let y = Math.max(0, cy - 3); y <= Math.min(size - 1, cy + 3); y += 1) {
    for (let x = Math.max(0, cx - 3); x <= Math.min(size - 1, cx + 3); x += 1) {
      const value = loss[y * size + x];
      if (value > 0.1) local += value;
    }
  }
  if (local < 1) return null;

  const centre = { x: cx, y: cy };
  const centreLabel = pointToLabel(centre, size);
  const region = regionName(centre, size);
  // If the hottest shift sits on the best move's own area, the "loss" is the influence that move
  // would have built — an opportunity cost, not damage done by the played move.
  if (bestPoint && chebyshev(centre, bestPoint) <= 3) {
    return {
      kind: "territory",
      text: `Most of the cost is the point you passed up: ${bestLabel} would have built roughly ${local.toFixed(
        1
      )} points of ${sideName(mover)} influence in the ${region}.`
    };
  }
  return {
    kind: "territory",
    text: `Compared with ${bestLabel}, the damage is concentrated around ${centreLabel} (${region}) — roughly ${local.toFixed(
      1
    )} points of expected territory shift toward ${opponentName(mover)} there.`
  };
}

// Insert the async territory note in its natural place: after reply/alternative, before the group
// warning. Returns a new report; the original is not mutated.
export function withTerritoryNote(report: MoveReport, note: ReportNote): MoveReport {
  const notes = report.notes.filter((existing) => existing.kind !== "territory");
  const groupIndex = notes.findIndex((existing) => existing.kind === "group");
  const insertAt = groupIndex >= 0 ? groupIndex : notes.length;
  return { ...report, notes: [...notes.slice(0, insertAt), note, ...notes.slice(insertAt)] };
}

export function buildMoveReport(input: MoveReportInput): MoveReport {
  const notes: ReportNote[] = [];
  let refutationIndex: number | undefined;

  const reply = replyNote(input);
  if (reply) {
    notes.push(reply.note);
    refutationIndex = reply.refutationIndex;
  }

  const alternative = alternativeNote(input);
  let alternativePreview: AlternativePreview | undefined;
  if (alternative) {
    notes.push(alternative);
    const best = input.before?.moves[0];
    if (input.boardBefore && best?.pv && best.pv.length > 0) {
      alternativePreview = { label: best.label, pv: best.pv, boardBefore: input.boardBefore, mover: input.mover };
    }
  }

  const group = playedGroupNote(input.boardAfter, input.size, input.playedPoint, input.mover, input.after.ownership);
  if (group) notes.push(group);

  return {
    moveLabel: input.playedLabel,
    mover: input.mover,
    grade: input.grade,
    verdict: verdictText(input),
    notes,
    refutationIndex,
    alternativePreview
  };
}
